import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SupervisorLayout from "../../components/layout/SupervisorLayout";
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Clock, 
  Camera,
  X,
  UserCheck,
  AlertTriangle,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { differenceInMinutes } from 'date-fns';
import { formatUK, toUKDate } from '../../lib/ukTime';
import { isShiftEndPassedUK } from '../../lib/shiftTime';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupervisorDashboard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [todayShift, setTodayShift] = useState(null);
  const [availableShiftsCount, setAvailableShiftsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [clockLoading, setClockLoading] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [allocatedBy, setAllocatedBy] = useState('');
  // For spontaneous (no-shift) clock-in, employee fills 3 fields then taps
  // Save which "locks" the answers and reveals the camera flow.
  const [allocatedPosition, setAllocatedPosition] = useState('');
  const [allocatedLocation, setAllocatedLocation] = useState('');
  const [spontaneousSaved, setSpontaneousSaved] = useState(false);
  const [lateReason, setLateReason] = useState('');
  const [shiftRole, setShiftRole] = useState('');
  // Banner dismissal — when the user clicks X we hide it locally for the
  // session. Backend will stop returning the field after 1h anyway.
  const [autoClockOutDismissed, setAutoClockOutDismissed] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTodayShift();
    fetchAvailableShiftsCount();
  }, []);

  const fetchTodayShift = async () => {
    try {
      // Use the new /shifts/today endpoint which handles UK timezone on server
      const response = await axios.get(`${API}/shifts/today`);
      const { shifts, uk_date } = response.data;
      
      // Get all today's shifts sorted by start_time (backend sorts them)
      const todayShifts = shifts.filter(s => s.day_label === 'today');
      const yesterdayShift = shifts.find(s => s.day_label === 'yesterday' && s.status === 'in_progress') || shifts.find(s => s.day_label === 'yesterday');
      const tomorrowShift = shifts.find(s => s.day_label === 'tomorrow');
      
      // Smart shift selection for multiple shifts per day:
      // 1. If currently clocked in (is_on_shift), pick the shift that is 'in_progress'
      // 2. Otherwise, pick the next shift that is NOT 'completed'
      // 3. If all completed, show the last completed one (for reference)
      let currentShift = null;
      const openYesterday = yesterdayShift && yesterdayShift.status === 'in_progress' ? yesterdayShift : null;

      if (todayShifts.length > 0) {
        const inProgress = todayShifts.find(s => s.status === 'in_progress');
        const nextScheduled = todayShifts.find(s => s.status === 'scheduled');
        
        if (inProgress) {
          // Currently working this shift
          currentShift = inProgress;
        } else if (openYesterday) {
          // Yesterday's shift is still OPEN — must clock out of it first.
          currentShift = openYesterday;
        } else if (nextScheduled) {
          // Next shift to work
          currentShift = nextScheduled;
        } else {
          // All completed — show the last one for reference
          currentShift = todayShifts[todayShifts.length - 1];
        }
      }
      
      // Fallback to yesterday/tomorrow if no today shifts
      if (!currentShift) {
        currentShift = yesterdayShift || tomorrowShift || null;
      }
      
      setTodayShift(currentShift);
    } catch (error) {
      console.error('Failed to fetch shift:', error);
      // Fallback to old method if new endpoint fails
      try {
        const today = formatUK(new Date().toISOString(), 'yyyy-MM-dd');
        const response = await axios.get(`${API}/shifts`, { params: { date: today } });
        // Same logic: pick first non-completed shift
        const allShifts = response.data || [];
        const next = allShifts.find(s => s.status !== 'completed') || allShifts[0] || null;
        setTodayShift(next);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableShiftsCount = async () => {
    try {
      const response = await axios.get(`${API}/open-shifts`);
      const available = response.data.filter(s => s.status === 'available');
      setAvailableShiftsCount(available.length);
    } catch (error) {
      console.error('Failed to fetch available shifts:', error);
    }
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateAllocatedBy = (name) => {
    if (!name || !name.trim()) return false;
    
    const userNameLower = user?.name?.toLowerCase().trim() || '';
    const allocatedLower = name.toLowerCase().trim();
    
    // Check if any part of user's name is in allocated_by
    const userParts = userNameLower.split(' ').filter(p => p.length > 2);
    for (const part of userParts) {
      if (allocatedLower.includes(part)) {
        return false;
      }
    }
    
    // Check if allocated_by parts are in user's name
    const allocatedParts = allocatedLower.split(' ').filter(p => p.length > 2);
    for (const part of allocatedParts) {
      if (userNameLower.includes(part)) {
        return false;
      }
    }
    
    return true;
  };

  // Check if current time is more than 1 hour late for clock in
  const isLateClockIn = () => {
    if (!todayShift?.start_time) return false;
    const ukNow = toUKDate(new Date().toISOString());
    const [hours, minutes] = todayShift.start_time.split(':').map(Number);
    const shiftStart = new Date(ukNow);
    shiftStart.setHours(hours, minutes, 0, 0);
    
    // For night shifts from yesterday, the start was yesterday
    if (todayShift.day_label === 'yesterday') {
      shiftStart.setDate(shiftStart.getDate() - 1);
    }
    
    const diffMins = differenceInMinutes(ukNow, shiftStart);
    return diffMins > 60; // More than 1 hour late
  };

  // Number of minutes from now until shift start (negative = already started).
  // Used by the strict 10-minute pre-clock-in gate.
  const minutesUntilShiftStart = () => {
    if (!todayShift?.start_time) return null;
    const ukNow = toUKDate(new Date().toISOString());
    const [hours, minutes] = todayShift.start_time.split(':').map(Number);
    const shiftStart = new Date(ukNow);
    shiftStart.setHours(hours, minutes, 0, 0);
    if (todayShift.day_label === 'tomorrow') {
      shiftStart.setDate(shiftStart.getDate() + 1);
    } else if (todayShift.day_label === 'yesterday') {
      shiftStart.setDate(shiftStart.getDate() - 1);
    }
    return differenceInMinutes(shiftStart, ukNow);
  };

  // True iff the employee is opening the app earlier than the 10-minute
  // pre-shift window. PENDING shifts (no start_time) are always allowed.
  const isTooEarlyToClockIn = () => {
    if (!todayShift?.start_time) return false;
    const m = minutesUntilShiftStart();
    return m !== null && m > 10;
  };

  // Check if current time is more than 1 hour past shift end for clock out
  const isLateClockOut = () => {
    if (!todayShift?.end_time) return false;
    const ukNow = toUKDate(new Date().toISOString());
    const [hours, minutes] = todayShift.end_time.split(':').map(Number);
    const shiftEnd = new Date(ukNow);
    shiftEnd.setHours(hours, minutes, 0, 0);
    
    // For night shifts, end time is the next day from shift date
    const startTime = todayShift.start_time || '00:00';
    if (todayShift.end_time < startTime) {
      // Night shift — if shift is from yesterday, end is today (correct already)
      // If shift is from today, end is tomorrow (don't flag as late yet)
      if (todayShift.day_label === 'today') {
        return false; // End time is tomorrow, not late yet
      }
    }
    
    const diffMins = differenceInMinutes(ukNow, shiftEnd);
    return diffMins > 60; // More than 1 hour past end
  };

  const handleClockIn = async () => {
    if (!photo) {
      toast.error('Please take a site photo before clocking in');
      return;
    }
    
    if (!shiftRole) {
      toast.error('Please select your shift role before clocking in');
      return;
    }
    
    // If late clock in (more than 1 hour), require reason
    if (isLateClockIn() && !lateReason.trim()) {
      toast.error('Please provide a reason for late clock in');
      return;
    }
    
    setClockLoading(true);
    try {
      const payload = { 
        shift_id: todayShift?.id,
        allocated_by: null,
        allocated_position: null,
        allocated_location: null,
        late_reason: isLateClockIn() ? lateReason.trim() : null,
        shift_role: shiftRole
      };
      
      const response = await axios.post(`${API}/clock/in`, payload);

      // Show success + reset UI IMMEDIATELY so the employee isn't stuck on
      // "Please wait..." while the photo (which can be a large base64) uploads.
      toast.success('Thank you and have a good shift');
      setPhoto(null);
      setAllocatedBy('');
      setAllocatedPosition('');
      setAllocatedLocation('');
      setSpontaneousSaved(false);
      setLateReason('');
      setShiftRole('');
      setClockLoading(false);
      await refreshUser();
      fetchTodayShift();

      // Fire-and-forget photo upload
      axios.post(`${API}/clock/photo`, {
        event_id: response.data.id,
        photo_base64: photo
      }).catch(() => {
        toast.error('Photo upload failed — your clock-in was saved');
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!photo) {
      toast.error('Please take a site photo before clocking out');
      return;
    }

    // If late clock out (more than 1 hour past shift end), require reason
    if (isLateClockOut() && !lateReason.trim()) {
      toast.error('Please provide a reason for late clock out');
      return;
    }

    setClockLoading(true);
    try {
      const response = await axios.post(`${API}/clock/out`, {
        shift_id: todayShift?.id,
        late_reason: isLateClockOut() ? lateReason.trim() : null
      });

      // Instant UI reset — see handleClockIn for rationale.
      toast.success('Thank you for your work');
      setPhoto(null);
      setLateReason('');
      setClockLoading(false);
      await refreshUser();
      fetchTodayShift();

      axios.post(`${API}/clock/photo`, {
        event_id: response.data.id,
        photo_base64: photo
      }).catch(() => {
        toast.error('Photo upload failed — your clock-out was saved');
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
      setClockLoading(false);
    }
  };

  const isOnShift = user?.is_on_shift;

  // FORGOT CLOCK OUT — still clocked in but the scheduled end has passed.
  // Exception: admin deleted the clock-out (shift cover) → normal flow.
  const forgotClockOut = isOnShift && todayShift && todayShift.status === 'in_progress' && !todayShift.reclock_out_actual && isShiftEndPassedUK(todayShift);

  const handleForgotClockOut = async () => {
    setClockLoading(true);
    try {
      await axios.post(`${API}/clock/confirm-forgot-out`, {});
      toast.success('Previous shift closed at the scheduled end time');
      setPhoto(null);
      setLateReason('');
      setClockLoading(false);
      await refreshUser();
      fetchTodayShift();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm clock out');
      setClockLoading(false);
    }
  };
  
  // Check if shift time has ended (using UK time)
  const isShiftEnded = () => {
    if (!todayShift || !todayShift.end_time) return false;
    const ukNow = toUKDate(new Date().toISOString());
    const currentTotalMinutes = ukNow.getHours() * 60 + ukNow.getMinutes();
    
    const [endHours, endMinutes] = todayShift.end_time.split(':').map(Number);
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    return currentTotalMinutes > endTotalMinutes;
  };

  const shiftEnded = isShiftEnded();

  if (loading) {
    return (
      <SupervisorLayout>
        <div className="py-8 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </SupervisorLayout>
    );
  }

  return (
    <SupervisorLayout>
      <div className="py-6 space-y-6">
        {/* AUTO CLOCK-OUT BANNER — visible only when backend returns
            `last_auto_clock_out_at` (within the last hour). Dismissible. */}
        {user?.last_auto_clock_out_at && !autoClockOutDismissed && (
          <Card className="bg-amber-900/20 border-amber-500/50 rounded-sm">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-semibold text-amber-300 uppercase tracking-wider">Clocked Out Automatically</p>
                <p className="text-sm text-amber-200/90">You have been clocked out automatically. Any questions please get in touch with admin.</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setAutoClockOutDismissed(true)}
                className="h-7 w-7 text-amber-300 hover:text-amber-200"
                data-testid="dismiss-auto-clockout-banner"
              >
                <X className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* SHIFT COMPLETED — shown after clock-out for today's scheduled shift.
            Blocks the clock-in/out UI so the dashboard returns to a clean,
            "done for today" state instead of looping back to a clock-in form. */}
        {!isOnShift && todayShift && todayShift.status === 'completed' && (
          <Card className="bg-green-900/20 border-green-500/40 rounded-sm">
            <CardContent className="p-5 text-center space-y-2">
              <UserCheck className="w-10 h-10 text-green-400 mx-auto" />
              <p className="text-base font-semibold text-green-300 uppercase tracking-wider">Shift Completed</p>
              <p className="text-sm text-green-200/90">
                You've clocked out for today's shift. Thank you for your work!
              </p>
              {todayShift.start_time && todayShift.end_time && (
                <p className="text-xs text-green-200/70 font-mono">
                  {todayShift.start_time} – {todayShift.end_time} @ {todayShift.site_name || 'site'}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* TOO EARLY GATE — shown when employee opens app well before the
            10-minute pre-shift window. Blocks the clock-in flow entirely. */}
        {!isOnShift && todayShift && todayShift.status !== 'completed' && isTooEarlyToClockIn() && (
          <Card className="bg-amber-900/20 border-amber-500/50 rounded-sm">
            <CardContent className="p-5 text-center space-y-2">
              <Clock className="w-8 h-8 text-amber-400 mx-auto" />
              <p className="text-base font-semibold text-amber-300 uppercase tracking-wider">Too Early to Clock In</p>
              <p className="text-sm text-amber-200/90">
                You can clock in only <strong>10 minutes before</strong> your shift starts ({todayShift.start_time}).
              </p>
              <p className="text-xs text-amber-200/70">
                Come back closer to your shift time.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Welcome */}
        <div className="text-center">
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Welcome back</p>
          <h1 className="text-2xl font-barlow tracking-widest mt-1">{user?.name}</h1>
          <p className="text-xs text-muted-foreground font-mono mt-2">
            {formatUK(new Date().toISOString(), 'EEEE, MMMM d, yyyy')}
          </p>
        </div>

        {/* My Shifts Section */}
        {/* Shift Role Selection — only when clocking in. Hide after the
            day's scheduled shift is already completed. */}
        {!isOnShift && !(todayShift && todayShift.status === 'completed') && (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Shift Role (Required)
              </p>
              <Select value={shiftRole} onValueChange={setShiftRole}>
                <SelectTrigger className="bg-background border-border" data-testid="shift-role-select">
                  <SelectValue placeholder="Select your role..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Cleaner">Cleaner</SelectItem>
                  <SelectItem value="Dual role (SIA, cleaner)">Dual role (SIA, cleaner)</SelectItem>
                  <SelectItem value="SIA Door Supervisor">SIA Door Supervisor</SelectItem>
                  <SelectItem value="Steward (CSO)">Steward (CSO)</SelectItem>
                  <SelectItem value="Event Supervisor">Event Supervisor</SelectItem>
                </SelectContent>
              </Select>
              {!shiftRole && (
                <p className="text-xs text-muted-foreground mt-2">
                  Select your role before taking a photo and clocking in
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Late Reason — when clocking in late.
            Skip if today's shift is already completed (already clocked out)
            so the dashboard returns to its neutral state. */}
        {!isOnShift && todayShift && todayShift.status !== 'completed' && isLateClockIn() && (
          <Card className="bg-amber-900/20 border-amber-600/50 rounded-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-amber-500">
                <AlertTriangle className="w-5 h-5" />
                <p className="text-sm font-medium">Late Clock In</p>
              </div>
              <p className="text-xs text-amber-200/70">
                You are clocking in more than 1 hour after your shift start time. Please provide a reason:
              </p>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-amber-400">
                  Reason for Late Clock In (Required)
                </Label>
                <Textarea
                  value={lateReason}
                  onChange={(e) => setLateReason(e.target.value)}
                  placeholder="e.g., Traffic delay, public transport issue, personal emergency..."
                  className="bg-background border-amber-600/50 rounded-sm min-h-[80px]"
                  data-testid="late-reason-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Late Reason — when clocking OUT late (more than 1h past shift end).
            Shown after the photo is taken so employee can fill in the reason
            before the Clock Out button becomes enabled. */}
        {isOnShift && photo && isLateClockOut() && (
          <Card className="bg-amber-900/20 border-amber-600/50 rounded-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-amber-500">
                <AlertTriangle className="w-5 h-5" />
                <p className="text-sm font-medium">Late Clock Out</p>
              </div>
              <p className="text-xs text-amber-200/70">
                You are clocking out more than 1 hour after your shift end time. Please provide a reason:
              </p>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-amber-400">
                  Reason for Late Clock Out (Required)
                </Label>
                <Textarea
                  value={lateReason}
                  onChange={(e) => setLateReason(e.target.value)}
                  placeholder="e.g., My supervisor (name) asked me to stay one extra hour, incident on site, handover delayed..."
                  className="bg-background border-amber-600/50 rounded-sm min-h-[80px]"
                  data-testid="late-clockout-reason-input"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoCapture}
          className="hidden"
        />

        {/* Single area: Camera OR Clock Button.
            The camera is GATED — only shown when:
              - employee is on shift (clock-out flow), OR
              - employee has a real shift and is NOT too early to clock in, OR
              - employee has no shift but has SAVED the spontaneous form
            This enforces the 10-minute pre-shift gate and the 3-field gate. */}
        {(() => {
          if (forgotClockOut) {
            return (
              <div className="py-4">
                <div className="border-2 border-primary bg-primary/10 rounded-md p-6 space-y-4" data-testid="forgot-clockout-panel">
                  <p className="text-center font-barlow text-2xl sm:text-3xl uppercase tracking-widest text-primary font-bold">
                    You forgot to clock out
                  </p>
                  <Button
                    onClick={handleForgotClockOut}
                    disabled={clockLoading}
                    data-testid="confirm-forgot-clockout-btn"
                    className="w-full h-24 rounded-md text-2xl font-barlow uppercase tracking-widest bg-primary text-primary-foreground hover:bg-yellow-300"
                  >
                    {clockLoading ? 'Please wait...' : 'Confirm Clock Out'}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    Your clock-out time will be recorded as your scheduled shift end ({todayShift.end_time}).
                  </p>
                </div>
              </div>
            );
          }
          const tooEarly = !isOnShift && todayShift && todayShift.status !== 'completed' && isTooEarlyToClockIn();
          const alreadyDoneToday = !isOnShift && todayShift && todayShift.status === 'completed';
          const needsSpontaneousForm =
            !isOnShift &&
            (!todayShift || !todayShift.start_time || !todayShift.end_time) &&
            !spontaneousSaved;
          if (tooEarly || needsSpontaneousForm || alreadyDoneToday) {
            return null;
          }
          return (
        <div className="py-4">
          {!photo ? (
            /* No photo yet — show camera + selfie hint */
            <>
              <p className="text-center text-sm text-primary uppercase tracking-wider mb-3 font-semibold" data-testid="selfie-message">
                Make a selfie on the camera
              </p>
              <div
                className="w-full h-48 rounded-md border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors bg-card"
                onClick={() => fileInputRef.current?.click()}
                data-testid="take-photo-area"
              >
                <Camera className="w-14 h-14 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground font-barlow uppercase tracking-wider">
                  {isOnShift ? 'Take photo to clock out' : 'Take photo to clock in'}
                </p>
              </div>
            </>
          ) : isOnShift ? (
            /* Photo taken + On duty → CLOCK OUT
               Disable button while a Late Clock Out reason is required but
               still empty, so employee can't bypass the requirement. */
            (() => {
              const lateOutBlocked = isLateClockOut() && !lateReason.trim();
              return (
                <Button
                  onClick={handleClockOut}
                  disabled={clockLoading || lateOutBlocked}
                  data-testid="clock-out-btn"
                  className={cn(
                    'w-full h-32 rounded-md text-3xl font-barlow uppercase tracking-widest transition-all bg-destructive hover:bg-red-600 text-foreground',
                    lateOutBlocked && 'opacity-40 cursor-not-allowed'
                  )}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Clock className="w-10 h-10" strokeWidth={1.5} />
                    {clockLoading
                      ? 'Please wait...'
                      : lateOutBlocked
                        ? 'Enter Late Reason First'
                        : 'Clock Out'}
                  </div>
                </Button>
              );
            })()
          ) : (!shiftRole || (todayShift && todayShift.status !== 'completed' && isLateClockIn() && !lateReason.trim())) ? (
            /* Photo taken but missing requirements — show what's needed */
            <div className="space-y-3">
              <Button
                disabled
                className="w-full h-32 rounded-md text-3xl font-barlow uppercase tracking-widest bg-primary text-primary-foreground opacity-40"
              >
                <div className="flex flex-col items-center gap-2">
                  <Clock className="w-10 h-10" strokeWidth={1.5} />
                  Clock In
                </div>
              </Button>
              <p className="text-xs text-center text-yellow-500">
                {!shiftRole
                  ? 'Select your role above'
                  : 'Provide late reason above'}
              </p>
            </div>
          ) : (
            /* Photo taken + all requirements met → CLOCK IN */
            <Button
              onClick={handleClockIn}
              disabled={clockLoading}
              data-testid="clock-in-btn"
              className="w-full h-32 rounded-md text-3xl font-barlow uppercase tracking-widest transition-all bg-primary hover:bg-yellow-300 text-primary-foreground tactical-glow"
            >
              <div className="flex flex-col items-center gap-2">
                <Clock className="w-10 h-10" strokeWidth={1.5} />
                {clockLoading ? 'Please wait...' : 'Clock In'}
              </div>
            </Button>
          )}
        </div>
          );
        })()}

      </div>
    </SupervisorLayout>
  );
}
